import React, { useEffect, useState } from "react"
import { FiArrowLeft, FiCalendar, FiCheck } from "react-icons/fi"
import { Link, Navigate, useParams, useNavigate } from "react-router-dom"
import LocateBed from "../../../../../assets/images/locate-bed.svg";
import createAxiosInstance from "../../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../../utils/api";
import useMyAgencyData from "../../../../../hooks/useMyAgencyData";
import useAgencies from "../../../../../hooks/useAgencies";
import { getElements, getOther, responseTextByType } from "../../../../../utils/helper";
import { formatDateTime } from "../../../../../utils/formatters";
import { showToast } from "../../../../../Components/ToastNotification";
import Loader from "../../../../../Components/Loader";
import useSocket from "../../../../../services/socketService";

const ReceiveDetail = () => {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const myShelter = useMyAgencyData();
  const { agenciesById: agencies } = useAgencies();

  const axiosInstance = createAxiosInstance();

  const [request, setRequest] = useState<any>({});
  const [isLoding, setIsLoding] = useState(true);

  const { on } = useSocket("requests");

  useEffect(() => {
    const unsubscribe = on("message", (data) => {
      if (data.eventType === "updated" || data.eventType === "canceled") {
        if (data.data.requestId === requestId) {
          showToast("An agency has changed the status of a request.", "info");
          getRequestDetails();
        }
      }
    });
  
    return unsubscribe;
  }, []);

  useEffect(() => {
    getRequestDetails();
  }, [myShelter]);

  if (!requestId || requestId === undefined || requestId === "" || requestId === null) {
    return <Navigate to="/bedfinder/beds-needed" replace />;
  }

  const usCanAswer = (groupId: string) => {
    return myShelter?.groupInfo?.find(
      it => groupId.includes(it.groupId) && it.canAnswerRequest,
    );
  }

  const getRequestDetails = async () => {
    try {
      const requestResponse = await axiosInstance.get(API_ENDPOINTS.BEDFINDER.GETREQUESTDETAILS.replace("{requestId}", requestId));

      const requests = requestResponse.data; // Extract the actual data

      const needResponse = await axiosInstance.get(API_ENDPOINTS.BEDFINDER.GETNEEDDETAILS.replace("{needId}", requests?.needId));

      const need = needResponse.data; // Extract the actual data

      const responseResult = await axiosInstance.get(API_ENDPOINTS.BEDFINDER.GETREQUESTRESPONSES.replace("{requestId}", requestId));

      const responses = responseResult.data; // Extract the actual data

      const currentResponse = responses.find(
        (it: any) => it.agencyId == myShelter?.agencyId
      );

      const isResponseVisible = currentResponse?.agencyId == myShelter?.agencyId;
      const assessment = need?.assessments[0];
      const canAnswer = usCanAswer(requests?.groupId);

      // console.log({requests, need, currentResponse, isResponseVisible, assessment, canAnswer});

      setRequest({ currentRequest: requests, need, currentResponse, isResponseVisible, assessment, canAnswer });
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const createResponse = async (answer: string, requestId: string) => {
    try {
      setIsLoding(true);

      const payload = {
        requestId: requestId,
        response: answer
      };

      const response = await axiosInstance.post(API_ENDPOINTS.BEDFINDER.SENDRESPONSE.replace("{requestId}", requestId), payload);

      if (response.status == 200) {
        getRequestDetails();
        showToast("The request was successfully sent.", "success");
      }
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      showToast("An error occurred while submitting the request. Please try again.", "error");
      console.log(error);
    }
  }

  // pagination page
  return (
    <>
      {isLoding && <Loader />}
      <div className=" bg-white">
        <div className="container py-4 md:py-10 2xl:py-[70px]">
          <div className="mb-4">
            <h3 className="text-gray-900 font-semibold text-base lg:text-lg 2xl:text-xl mb-2">Request from: {agencies[request?.currentRequest?.agencyId]?.name ?? "unknown"}</h3>
          </div>
          {request?.isResponseVisible && (
            <div className="mt-2 bg-[#D4EAFC] text-blue-600 rounded-lg p-3 xl:p-4" >
              <div className="flex gap-1 items-center">
                <FiCheck size={24} />
                <span className="text-sm 2xl:text-base">You responded: {responseTextByType[request?.currentResponse?.response ?? "null"]}</span>
              </div>
            </div>
          )}
          <div className="card p-0 mt-6 overflow-hidden">
            <div className="card-header flex flex-wrap items-center justify-between p-3 lg:p-5 border border-b text-gray-600 text-xs lg:text-sm 2xl:text-base font-medium gap-3">
              <div className="flex flex-wrap items-center space-x-4">
                <div className="flex items-center gap-3">
                  <img src={LocateBed} alt="LocateBed" className="w-8" />
                  <div>
                    Request #: <Link to={'#'} className="font-semibold">{request?.currentRequest?.nr}</Link>
                  </div>
                </div>
                <span className="text-blue-100">|</span>
                {request?.currentRequest?.status === "open" && (
                  <span className="bg-success50 text-success text-xs 2xl:text-sm p-1 px-2 rounded-full flex items-center gap-1 w-fit font-medium">
                    Open
                  </span>
                )}
                {request?.currentRequest?.status === "closed" && (
                  <span className="bg-orange-50 text-error text-xs 2xl:text-sm p-1 px-2 rounded-full flex items-center gap-1 w-fit font-medium">
                    Closed
                  </span>
                )}
              </div>
              <div className="flex items-center">
                <FiCalendar className="me-3" size={20} />
                <span>{request?.currentRequest && formatDateTime(request?.currentRequest?.createdAt)}</span>
              </div>
            </div>
            {request?.currentRequest?.status == "open" && (
              <div className="card-body p-3 lg:p-5">
                <div className="border rounded-xl p-4">
                  <div className="fix-block">
                    {request?.assessment.age && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Age : <span className="font-semibold to-gray-800 ms-2">{request?.assessment.age}</span>
                      </div>
                    )}
                    {(request?.assessment.accompanyingChildren?.length ?? 0) > 0 && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Children : <span className="font-semibold to-gray-800 ms-2">{getElements(request?.assessment.accompanyingChildren ?? [])}</span>
                      </div>
                    )}
                    {request?.assessment.accompanyingChildrenDetails &&
                      request?.assessment.accompanyingChildrenDetails.map((child: any, __id: any) => (
                        <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4" key={"details" + __id}>
                          <div>
                            <span>
                              Age  : <span className="font-semibold to-gray-800 ms-2">{child.age}</span>
                            </span>
                          </div>
                        </div>
                      ))}
                    {getElements(request?.assessment.history) && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Violence/aggression/self-harming behaviors :<span className="font-semibold to-gray-800 ms-2">{getElements(request?.assessment.history)}</span>
                      </div>
                    )}
                    {request?.assessment.mentalHealth && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Current mental health :<span className="font-semibold to-gray-800 ms-2">{request?.assessment.mentalHealth}</span>
                      </div>
                    )}
                    {request?.assessment.currentSubstanceUse && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Current substance use :<span className="font-semibold to-gray-800 ms-2">{request?.assessment.currentSubstanceUse}</span>
                      </div>
                    )}
                    {request?.assessment.primaryLanguage && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Primary Language :<span className="font-semibold to-gray-800 ms-2">{request?.assessment.primaryLanguage}</span>
                      </div>
                    )}
                    {request?.assessment.secondaryLanguage && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Secondary Language :<span className="font-semibold to-gray-800 ms-2">{request?.assessment.secondaryLanguage}</span>
                      </div>
                    )}
                    {getOther(request?.assessment, "languageSpoken") && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Language spoken :<span className="font-semibold to-gray-800 ms-2">{getOther(request?.assessment, "languageSpoken")}</span>
                      </div>
                    )}
                    {request?.assessment.englishProficiency && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        English Proficiency :<span className="font-semibold to-gray-800 ms-2">{request?.assessment.englishProficiency}</span>
                      </div>
                    )}
                    {getOther(request?.assessment, "traffickingSituation") && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Type of trafficking or violence :<span className="font-semibold to-gray-800 ms-2">{getOther(request?.assessment, "traffickingSituation")}</span>
                      </div>
                    )}
                    {getOther(request?.assessment, "physicalDisability") && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        Physical disability or mobility concerns :<span className="font-semibold to-gray-800 ms-2">{getOther(request?.assessment, "physicalDisability")}</span>
                      </div>
                    )}
                    {request?.assessment?.additionalInformation && (
                      <div className="text-xs 2xl:text-sm text-gray-700 font-normal mb-4">
                        What other information about your client and their situation is helpful in finding appropriate shelter for them? (Optional) :<span className="font-semibold to-gray-800 ms-2">{request?.assessment?.additionalInformation}</span>
                      </div>
                    )}
                  </div>
                  {request.canAnswer && (
                    <>
                      <div className="mb-4 mt-5">
                        <h3 className="text-gray-900 font-semibold text-sm lg:text-lg 2xl:text-xl mb-2">{request?.currentResponse ? "Change your response" : "Submit response"}</h3>
                      </div>
                      <div className="bottom-block">
                        <p className="text-gray-900 my-4">We may have space:</p>
                        <div className="flex space-x-3">
                          <button className="btn btn-outline-gray px-4 text-xs 2xl:text-sm py-2 md:min-w-24" onClick={() => createResponse("possible", request?.currentRequest.requestId)} disabled={isLoding}>Now</button>
                          <button className="btn btn-outline-gray px-4 text-xs 2xl:text-sm py-2 md:min-w-24" onClick={() => createResponse("possible_soon", request?.currentRequest.requestId)} disabled={isLoding}>Soon</button>
                          <button className="btn btn-outline-gray px-4 text-xs 2xl:text-sm py-2 md:min-w-24" onClick={() => createResponse("no", request?.currentRequest.requestId)} disabled={isLoding}>Not Now</button>
                        </div>
                        <p className="mt-3">
                          Keep in mind: if you click <strong>Now</strong> or <strong>Soon</strong>, the agency or survivor will contact you to make sure your shelter is a good fit.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="text-end">
            <button
              type="button"
              className="min-h-[38px] min-w-24 py-2 bg-white border border-borderGray px-3 inline-flex justify-center items-center gap-x-2 text-sm rounded-lg mt-4"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft />
              <span>Back</span>
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ReceiveDetail